import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { issueReportAPI } from '../../services';
import PaginationComponent from '../../components/PaginationComponent';
import { toast } from 'react-toastify';
import useFilterParams from '../../hooks/useFilterParams';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Chờ duyệt' },
  { value: 'APPROVED', label: 'Đã duyệt' },
  { value: 'REJECTED', label: 'Từ chối' },
  { value: 'COMPLETED', label: 'Đã xử lý' },
  { value: '', label: 'Tất cả' },
];

const STATUS_BADGE = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  COMPLETED: 'info',
};

const formatDateTime = (value, pattern = 'dd/MM/yyyy HH:mm') => {
  if (!value) return '-';
  try {
    return format(new Date(value), pattern);
  } catch {
    return value;
  }
};

const parseListResponse = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return { items: payload, total: payload.length, page: 1, limit: 20, total_pages: 1 };
  const items = Array.isArray(payload?.data) ? payload.data : [];
  return {
    items,
    total: payload?.total ?? items.length,
    page: payload?.page ?? 1,
    limit: payload?.limit ?? 20,
    total_pages: payload?.total_pages ?? 1,
  };
};

/**
 * #191439 — Màn này là ĐÍCH của link sâu trong tin báo cáo sự cố (dòng 188 + 189 của bảng
 * khảo sát #132320). Tin gửi kèm `?branch_id=&id=<reportId>&status=ALL` (#210029 — trước đây
 * server QUÊN gửi `status`, xem ghi chú ALL_STATUS_PARAM dưới).
 *
 * `id` KHÔNG phải bộ lọc gửi lên API (`getIssueReports` không có tham số `id`): nó là "mở
 * thẳng đúng bản ghi" theo `KHUON-CHUAN-URL-PARAM.md` mục 1. Ở màn này việc đó nghĩa là làm
 * NỔI BẬT đúng dòng báo cáo và cuộn tới nó, để người nhận thấy ngay trong danh sách thay vì
 * phải dò. Không tìm thấy trong trang hiện tại thì im lặng bỏ qua — vẫn còn bộ lọc trạng thái
 * đưa họ tới đúng nhóm, không có gì gãy.
 */
// #210029 — "mọi trạng thái" trên URL viết là `status=ALL`, KHÔNG phải `status=` rỗng: param
// rỗng bị `getParam` coi như không có ⇒ rơi về mặc định `PENDING` (đúng lỗi Nặng 2 của #202611),
// và `buildDeepLink` bên server cũng bỏ hẳn param rỗng nên không gửi ra được.
const ALL_STATUS_PARAM = 'ALL';

const ISSUE_REPORT_FILTER_SPEC = {
  status: {
    default: 'PENDING',
    parse: (v) => (v.toUpperCase() === ALL_STATUS_PARAM ? '' : v.toUpperCase()),
    valid: (v) =>
      v.toUpperCase() === ALL_STATUS_PARAM ||
      STATUS_OPTIONS.some((o) => o.value && o.value === v.toUpperCase()),
    // Người dùng tự chọn "Tất cả" cũng ghi ra `status=ALL` để F5 giữ nguyên màn hình (luật 6
    // của useFilterParams) — trước đây ghi chuỗi rỗng nên tải lại là nhảy về "Chờ duyệt".
    serialize: (v) => (v === '' ? ALL_STATUS_PARAM : String(v ?? '')),
  },
  employee_id: { default: '' },
  id: { default: '' },
};

const IssueReportManagementPage = () => {
  const { selectedBranch } = useBranch();

  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] = useFilterParams(
    ISSUE_REPORT_FILTER_SPEC,
  );
  const statusFilter = urlFilters.status;
  const employeeIdFilter = urlFilters.employee_id;
  const highlightId = urlFilters.id;
  // #210029 — nhớ đã tự nới bộ lọc cho `id` nào rồi, để chỉ nới đúng một lần.
  const daNoiLocRef = useRef(null);
  const setStatusFilter = (v) => setUrlFilters({ status: v });
  const setEmployeeIdFilter = (v) => setUrlFilters({ employee_id: v });
  const [reports, setReports] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [approveModal, setApproveModal] = useState({ show: false, report: null });
  const [rejectModal, setRejectModal] = useState({ show: false, report: null });
  const [bonusPercent, setBonusPercent] = useState(5);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const branchId = selectedBranch?.id;

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const params = {
        status: statusFilter || undefined,
        branch_id: branchId,
        employee_id: employeeIdFilter.trim() || undefined,
        page: meta.page,
        limit: meta.limit,
      };
      const response = await issueReportAPI.getIssueReports(params);
      const { items, total, page, limit, total_pages } = parseListResponse(response);
      setReports(items);
      setMeta({ total, page, limit, total_pages });
    } catch (error) {
      console.error('Cannot load issue reports', error);
      toast.error(error?.message || 'Không tải được danh sách báo cáo sự cố');
      setLoadError(error?.message || 'Không tải được danh sách báo cáo sự cố');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, branchId, employeeIdFilter, meta.page, meta.limit]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // #191439: cuộn tới đúng báo cáo mà link sâu trỏ tới, SAU khi danh sách đã vẽ xong.
  //
  // #210029: link trỏ tới báo cáo KHÔNG nằm trong bộ lọc đang xem (điển hình: link cũ không
  // kèm `status`, mở lại sau khi báo cáo đã Duyệt/Từ chối) thì tự nới bộ lọc về "Tất cả" ĐÚNG
  // MỘT LẦN rồi tải lại — để không phải bắt người nhận tự đổi ô lọc. Nới rồi vẫn không thấy
  // (báo cáo nằm ở trang sau) thì thôi, không nới nữa, không lặp.
  useEffect(() => {
    if (!highlightId || loading || !reports.length) return;
    const el = document.getElementById(`issue-report-${highlightId}`);
    if (el) {
      el.scrollIntoView({ block: 'center' });
      return;
    }
    if (statusFilter !== '' && daNoiLocRef.current !== highlightId) {
      daNoiLocRef.current = highlightId;
      setUrlFilters({ status: '' });
      setMeta((prev) => ({ ...prev, page: 1 }));
    }
  }, [highlightId, loading, reports, statusFilter, setUrlFilters]);

  const handlePageChange = (page) => {
    setMeta((prev) => ({ ...prev, page }));
  };

  const handleApprove = async () => {
    const report = approveModal.report;
    if (!report?.id) return;
    try {
      setActionLoading(true);
      await issueReportAPI.approveIssueReport(report.id, {
        bonus_percent: Number(bonusPercent) || 0,
      });
      toast.success('Đã duyệt báo cáo sự cố');
      setApproveModal({ show: false, report: null });
      setBonusPercent(5);
      fetchReports();
    } catch (error) {
      toast.error(error?.message || 'Duyệt thất bại');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    const report = rejectModal.report;
    if (!report?.id) return;
    try {
      setActionLoading(true);
      await issueReportAPI.rejectIssueReport(report.id, {
        reason: rejectReason.trim() || undefined,
      });
      toast.success('Đã từ chối báo cáo sự cố');
      setRejectModal({ show: false, report: null });
      setRejectReason('');
      fetchReports();
    } catch (error) {
      toast.error(error?.message || 'Từ chối thất bại');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Quản lý báo cáo sự cố</h1>
            <p className="text-muted mb-0 small">
              Xem và duyệt/từ chối báo cáo sự cố của nhân viên. Duyệt có thể cộng % tích lũy thưởng cá nhân.
            </p>
          </div>
          <Button variant="primary" onClick={fetchReports} disabled={loading}>
            Làm mới
          </Button>
        </div>

        <InvalidParamsNotice
          ignored={ignored}
          onDismiss={dismissIgnored}
        />
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex flex-wrap gap-3 align-items-center">
              <Form.Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setMeta((prev) => ({ ...prev, page: 1 }));
                }}
                style={{ minWidth: 140 }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control
                placeholder="Lọc theo ID nhân viên"
                value={employeeIdFilter}
                onChange={(e) => {
                  setEmployeeIdFilter(e.target.value);
                  setMeta((prev) => ({ ...prev, page: 1 }));
                }}
                style={{ maxWidth: 200 }}
              />
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" size="sm" /> Đang tải...
              </div>
            ) : loadError ? (
              <div className="text-center text-danger py-5">
                Không tải được danh sách báo cáo sự cố: {loadError}
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center text-muted py-5">Không có báo cáo sự cố nào.</div>
            ) : (
              <div className="table-responsive">
                <Table striped hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Nhân viên</th>
                      <th>Chi nhánh</th>
                      <th>Tiêu đề</th>
                      <th>Mô tả</th>
                      <th>Ngày báo cáo</th>
                      <th>Trạng thái</th>
                      <th className="text-end">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((row) => {
                      const r = row;
                      return (
                        // #191439: dòng được link sâu trỏ tới thì bôi nền + gắn `id` DOM để
                        // cuộn tới. `table-warning` là lớp sẵn có của react-bootstrap.
                        <tr
                          key={r.id}
                          id={`issue-report-${r.id}`}
                          className={highlightId && r.id === highlightId ? 'table-warning' : undefined}
                        >
                          <td>{r.employee_name ?? r.employee_id ?? '-'}</td>
                          <td>{r.branch_name ?? r.branch_id ?? '-'}</td>
                          <td>
                            <strong>{r.title || '-'}</strong>
                          </td>
                          <td className="text-muted small" style={{ maxWidth: 200 }}>
                            {r.description ? (r.description.length > 80 ? `${r.description.slice(0, 80)}...` : r.description) : '-'}
                          </td>
                          <td>{formatDateTime(r.created_at)}</td>
                          <td>
                            <Badge bg={STATUS_BADGE[r.status] || 'secondary'}>
                              {STATUS_OPTIONS.find((o) => o.value === r.status)?.label ?? r.status}
                            </Badge>
                            {r.bonus_percent != null && r.bonus_percent > 0 && (
                              <span className="ms-1 small text-success">+{r.bonus_percent}%</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex justify-content-end gap-2">
                              {r.status === 'PENDING' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={() => setApproveModal({ show: true, report: r })}
                                  >
                                    Duyệt
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => {
                                      setRejectReason('');
                                      setRejectModal({ show: true, report: r });
                                    }}
                                  >
                                    Từ chối
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
          {meta.total_pages > 1 && (
            <Card.Footer className="bg-white">
              <PaginationComponent
                totalItems={meta.total}
                itemsPerPage={meta.limit}
                currentPage={meta.page}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </Card.Footer>
          )}
        </Card>
      </div>

      <Modal
        show={approveModal.show}
        onHide={() => setApproveModal({ show: false, report: null })}
      >
        <Modal.Header closeButton>
          <Modal.Title>Duyệt báo cáo sự cố</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {approveModal.report && (
            <>
              <p className="mb-2">
                <strong>{approveModal.report.title}</strong>
              </p>
              <p className="text-muted small mb-3">
                {approveModal.report.employee_name} · {approveModal.report.branch_name}
              </p>
              <Form.Group>
                <Form.Label>Bonus % tích lũy thưởng cá nhân (0–100)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  value={bonusPercent}
                  onChange={(e) => setBonusPercent(Number(e.target.value) || 0)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setApproveModal({ show: false, report: null })}>
            Hủy
          </Button>
          <Button variant="success" onClick={handleApprove} disabled={actionLoading}>
            {actionLoading ? 'Đang xử lý...' : 'Duyệt'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={rejectModal.show}
        onHide={() => setRejectModal({ show: false, report: null })}
      >
        <Modal.Header closeButton>
          <Modal.Title>Từ chối báo cáo sự cố</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rejectModal.report && (
            <>
              <p className="mb-2">
                <strong>{rejectModal.report.title}</strong>
              </p>
              <p className="text-muted small mb-3">
                {rejectModal.report.employee_name} · {rejectModal.report.branch_name}
              </p>
              <Form.Group>
                <Form.Label>Lý do từ chối (tuỳ chọn, tối đa 500 ký tự)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  maxLength={500}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="VD: Sự cố nhỏ, không ảnh hưởng nghiệp vụ"
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRejectModal({ show: false, report: null })}>
            Hủy
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={actionLoading}>
            {actionLoading ? 'Đang xử lý...' : 'Từ chối'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
};

export default IssueReportManagementPage;
